import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router";
import { motion } from "framer-motion";
import { ArrowRight, Check, Loader2, Sparkles, Zap } from "lucide-react";
import { FormattedMessage, useIntl } from "react-intl";
import { toast } from "sonner";

import { type BillingPlanId } from "@/components/billing/billing-state";
import { BillingPlanModal } from "@/components/billing/billing-plan-modal";
import { SocialPostPreviewCard } from "@/components/campaign/social-post-preview-card";
import { CenteredLoader } from "@/components/common";
import { buildBrandPath } from "@/hooks/use-route-brand";
import { normalizeStandaloneScheduledPosts } from "@/lib/campaign-posts";
import { OnboardingPostingRhythmModal } from "@/components/onboarding/onboarding-posting-rhythm-modal";
import { getCalendarPostReviewActions } from "@/lib/calendar-posts";
import { buildFocusedPostEditorPath } from "@/lib/focused-post-editor";
import { ONBOARDING_EXTRA_DRAFT_PROMPT_MARKER } from "@/lib/onboarding-extra-draft";
import { formatInstagramProfileLabel } from "@/lib/social-post-chrome";
import { toStrategyEditorState } from "@/lib/strategy";
import { resolveScheduledPostPreview } from "@/lib/scheduled-post-preview";
import { trpc } from "@/lib/trpc";
import { Button } from "@runcheetah/ui";

const UPGRADE_QUERY_KEY = "upgrade";
const UPGRADE_PLAN_QUERY_KEY = "plan";

type UpgradeStep = "plans" | "posting-rhythm";

const BTN_BASE: React.CSSProperties = {
  all: "unset" as const,
  display: "inline-flex",
  alignItems: "center",
  gap: 6,
  padding: "8px 14px",
  borderRadius: 999,
  fontSize: 12.5,
  fontWeight: 800,
  letterSpacing: "-0.01em",
  cursor: "pointer",
  userSelect: "none",
  boxShadow: "0 8px 22px -10px rgba(13,31,45,.55)",
  transition: "opacity .15s ease, transform .15s ease",
};

const STEPS = [
  {
    labelId: "brandOnboarding.generating.step.communicates",
    labelDefault: "Learning how your brand communicates",
    helperId: "brandOnboarding.generating.step.communicatesHelper",
    helperDefault: "Tone, voice, audience",
  },
  {
    labelId: "brandOnboarding.generating.step.draftingIdeas",
    labelDefault: "Drafting your first ideas",
    helperId: "brandOnboarding.generating.step.draftingIdeasHelper",
    helperDefault: "Headlines and captions",
  },
  {
    labelId: "brandOnboarding.generating.step.matchingVisuals",
    labelDefault: "Matching visuals to your identity",
    helperId: "brandOnboarding.generating.step.matchingVisualsHelper",
    helperDefault: "Imagery, color, layout",
  },
] as const;

const STEP_MS = 1500;
const HOLD_MS = 500;
const FIRST_POST_COUNT = 2;

const NAVY = "#0D1F2D";
const MINT = "#C8FF00";
const TEXT_MUTED = "rgba(13,31,45,0.55)";
const BORDER_COLOR = "rgba(13,31,45,0.1)";
const FONT_MONO = "'JetBrains Mono', 'Fira Mono', ui-monospace, monospace";
const FONT_SERIF = "Georgia, 'Times New Roman', serif";

type GeneratedPost = ReturnType<typeof normalizeStandaloneScheduledPosts>[number];

const hasGeneratedImageAsset = (post: GeneratedPost): boolean =>
  (post.output.latestCompleted?.assets ?? []).some((asset) => Boolean(asset.signedUrl));

const isPostPreviewReady = (post: GeneratedPost): boolean =>
  post.status === "generated" &&
  Boolean(post.output.latestCompleted) &&
  hasGeneratedImageAsset(post);

const areFirstPostsReady = (posts: GeneratedPost[]): boolean => {
  const firstPosts = posts.slice(0, FIRST_POST_COUNT);
  return firstPosts.length === FIRST_POST_COUNT && firstPosts.every(isPostPreviewReady);
};

const formatPostTimeLabel = (scheduledFor: string): string =>
  new Intl.DateTimeFormat(undefined, {
    weekday: "short",
    hour: "numeric",
    minute: "2-digit",
  })
    .format(new Date(scheduledFor))
    .replace(",", " ·");

function ExtraDraftLoadingCard() {
  return (
    <div className="flex w-full flex-col items-center justify-center rounded-2xl border border-dashed border-[#c9d1dc] bg-[#fbfcfd] px-8 py-12 text-center">
      <span className="flex h-14 w-14 items-center justify-center rounded-full border border-[#d7dde6] bg-white text-[#0D1F2D] shadow-sm">
        <Loader2 className="h-6 w-6 animate-spin" aria-hidden="true" />
      </span>
      <span className="mt-5 text-sm font-semibold tracking-tight text-[#0D1F2D]">
        <FormattedMessage
          id="brandOnboarding.generating.extraDraftLoadingTitle"
          defaultMessage="Generating draft"
        />
      </span>
      <span className="mt-2 max-w-[15rem] text-xs leading-6 text-[#6b7280]">
        <FormattedMessage
          id="brandOnboarding.generating.extraDraftLoadingDescription"
          defaultMessage="Shaping another proof-driven concept from your onboarding profile."
        />
      </span>
    </div>
  );
}

function ExtraDraftRetryCard({
  errorMessage,
  onRetry,
}: {
  errorMessage: string;
  onRetry: () => void;
}) {
  return (
    <div className="flex w-full flex-col items-center justify-center rounded-2xl border border-dashed border-[#c9d1dc] bg-white px-8 py-12 text-center">
      <span className="text-sm font-semibold tracking-tight text-[#0D1F2D]">
        <FormattedMessage
          id="brandOnboarding.generating.extraDraftRetryTitle"
          defaultMessage="Preview unavailable"
        />
      </span>
      <span className="mt-2 max-w-[15rem] text-xs leading-6 text-[#6b7280]">{errorMessage}</span>
      <Button type="button" variant="outline" className="mt-5" onClick={onRetry}>
        <FormattedMessage
          id="brandOnboarding.generating.extraDraftRetryButton"
          defaultMessage="Retry loading"
        />
      </Button>
    </div>
  );
}

function UpgradeToGenerateMoreCard({ onOpen }: { onOpen: () => void }) {
  return (
    <div className="flex w-full flex-col items-center justify-center rounded-2xl border border-[#0D1F2D] bg-[radial-gradient(circle_at_top,rgba(200,255,0,0.18),rgba(255,255,255,0)_42%),#ffffff] px-8 py-12 text-center shadow-[0_8px_24px_rgba(13,31,45,0.08)]">
      <span className="flex h-14 w-14 items-center justify-center rounded-2xl bg-[#C8FF00] text-[#0D1F2D] shadow-sm">
        <Zap className="h-6 w-6" aria-hidden="true" />
      </span>
      <span className="mt-5 text-[1.05rem] font-semibold tracking-tight text-[#0D1F2D]">
        <FormattedMessage
          id="brandOnboarding.generating.upgradeTitle"
          defaultMessage="Upgrade to generate more"
        />
      </span>
      <span className="mt-3 max-w-[15rem] text-sm leading-6 text-[#6b7280]">
        <FormattedMessage
          id="brandOnboarding.generating.upgradeDescription"
          defaultMessage="Use runs when you are ready to generate more posts."
        />
      </span>
      <Button
        type="button"
        onClick={onOpen}
        className="mt-6 h-11 rounded-full bg-[#0D1F2D] px-6 text-sm font-semibold text-white hover:bg-[#13283a]"
      >
        <FormattedMessage id="brandOnboarding.generating.upgradeButton" defaultMessage="Upgrade" />
        <ArrowRight className="h-4 w-4" />
      </Button>
    </div>
  );
}

function GeneratingDraftsMock({ active, totalSteps }: { active: number; totalSteps: number }) {
  const revealedCount = Math.min(totalSteps, Math.max(0, active));

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.15, duration: 0.5 }}
      style={{
        background: "#fff",
        borderRadius: 20,
        border: `1px solid ${BORDER_COLOR}`,
        boxShadow:
          "0 30px 60px -20px rgba(13,31,45,0.14), 0 8px 22px -8px rgba(13,31,45,0.06), inset 0 1px 0 rgba(255,255,255,0.6)",
        padding: "16px 18px 18px",
        overflow: "hidden",
        position: "relative",
      }}
      data-testid="generating-mock"
    >
      <style>{`
        @keyframes ofGenSweep { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        @keyframes ofGenRingPulse {
          0%, 100% { transform: scale(1); opacity: .55; }
          50%      { transform: scale(1.04); opacity: 1; }
        }
        @keyframes ofGenCounterFade { from { opacity: .4; transform: translateY(2px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes ofGenDotPulse {
          0%, 100% { opacity: .55; transform: scale(.85); }
          50%      { opacity: 1;   transform: scale(1.15); }
        }
      `}</style>
      <div
        style={{
          minHeight: 360,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: 28,
          padding: "32px 24px",
        }}
      >
        <div
          aria-hidden
          style={{
            position: "relative",
            width: 200,
            height: 200,
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <span
            style={{
              position: "absolute",
              inset: 0,
              borderRadius: "50%",
              border: "1.5px solid rgba(200,255,0,.35)",
              animation: "ofGenRingPulse 2.4s ease-in-out infinite",
            }}
          />
          <span
            style={{
              position: "absolute",
              inset: 24,
              borderRadius: "50%",
              border: "1.5px solid rgba(200,255,0,.55)",
              animation: "ofGenRingPulse 2.4s ease-in-out infinite",
              animationDelay: ".4s",
            }}
          />
          <span
            style={{
              position: "absolute",
              inset: 48,
              borderRadius: "50%",
              border: "2px solid rgba(200,255,0,.85)",
              animation: "ofGenRingPulse 2.4s ease-in-out infinite",
              animationDelay: ".8s",
            }}
          />
          <span
            style={{
              position: "absolute",
              inset: 0,
              borderRadius: "50%",
              background:
                "conic-gradient(from 0deg, rgba(200,255,0,0) 0deg, rgba(200,255,0,.85) 60deg, rgba(200,255,0,0) 120deg)",
              WebkitMask:
                "radial-gradient(circle at center, transparent 70px, #000 71px, #000 99px, transparent 100px)",
              mask: "radial-gradient(circle at center, transparent 70px, #000 71px, #000 99px, transparent 100px)",
              animation: "ofGenSweep 1.6s linear infinite",
            }}
          />
          <div
            key={revealedCount}
            style={{
              position: "relative",
              width: 92,
              height: 92,
              borderRadius: "50%",
              background: "#fff",
              border: "1px solid rgba(13,31,45,.06)",
              boxShadow: "0 10px 30px -12px rgba(13,31,45,.18)",
              display: "inline-flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              animation: "ofGenCounterFade .35s ease-out both",
            }}
          >
            <div
              style={{
                fontFamily: FONT_MONO,
                fontSize: 24,
                fontWeight: 800,
                letterSpacing: "-0.02em",
                color: NAVY,
                lineHeight: 1,
              }}
            >
              {revealedCount}
              <span style={{ color: "rgba(13,31,45,.35)", fontWeight: 700 }}>/{totalSteps}</span>
            </div>
            <div
              style={{
                marginTop: 4,
                fontSize: 9.5,
                fontWeight: 700,
                letterSpacing: "0.08em",
                textTransform: "uppercase",
                color: TEXT_MUTED,
              }}
            >
              <FormattedMessage id="brandOnboarding.generating.ready" defaultMessage="Ready" />
            </div>
          </div>
        </div>

        <div
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 8,
            padding: "6px 14px 6px 10px",
            borderRadius: 999,
            background: "rgba(200,255,0,0.18)",
            border: "1px solid rgba(200,255,0,0.5)",
            fontFamily: FONT_MONO,
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: "0.04em",
            color: NAVY,
          }}
        >
          <span
            style={{
              width: 7,
              height: 7,
              borderRadius: 999,
              background: NAVY,
              animation: "ofGenDotPulse 1.4s ease-in-out infinite",
            }}
          />
          <FormattedMessage
            id="brandOnboarding.generating.generatingDrafts"
            defaultMessage="Generating drafts"
          />
        </div>
      </div>
    </motion.div>
  );
}

function PostCardWithOverlay({
  post,
  preview,
  timeLabel,
  profileLabel,
  actions,
  editPath,
  approving,
  rejecting,
  onApprove,
}: {
  post: { id: string; approvalStatus: string };
  preview: ReturnType<typeof resolveScheduledPostPreview>;
  timeLabel: string;
  profileLabel: string;
  actions: ReturnType<typeof getCalendarPostReviewActions>;
  editPath: string;
  approving: boolean;
  rejecting: boolean;
  onApprove: () => void;
}) {
  const intl = useIntl();
  const [hovered, setHovered] = useState(false);

  return (
    <div
      style={{ position: "relative" }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <SocialPostPreviewCard
        preview={preview}
        timeLabel={timeLabel}
        profileLabel={profileLabel}
        approvalLabel={
          post.approvalStatus === "approved"
            ? intl.formatMessage({
                id: "brandOnboarding.generating.approved",
                defaultMessage: "Approved",
              })
            : undefined
        }
        showApprovalBadge={post.approvalStatus === "approved"}
      />
      {actions.canApprove && (
        <div
          style={{
            position: "absolute",
            inset: 0,
            borderRadius: 16,
            background: "rgba(13,31,45,.42)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 10,
            opacity: hovered ? 1 : 0,
            pointerEvents: hovered ? "auto" : "none",
            transition: "opacity .22s ease",
          }}
        >
          <Link
            to={editPath}
            style={{
              ...BTN_BASE,
              background: "#fff",
              color: NAVY,
              transform: hovered ? "translateY(0)" : "translateY(4px)",
            }}
          >
            <svg
              width="13"
              height="13"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
              <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
            </svg>
            <FormattedMessage id="brandOnboarding.generating.edit" defaultMessage="Edit" />
          </Link>
          <button
            type="button"
            disabled={approving || rejecting}
            onClick={onApprove}
            style={{
              ...BTN_BASE,
              background: MINT,
              color: NAVY,
              transform: hovered ? "translateY(0)" : "translateY(4px)",
              opacity: approving || rejecting ? 0.6 : 1,
            }}
          >
            <svg
              width="13"
              height="13"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="3"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <polyline points="20 6 9 17 4 12" />
            </svg>
            {approving ? (
              <FormattedMessage
                id="brandOnboarding.generating.approving"
                defaultMessage="Approving…"
              />
            ) : (
              <FormattedMessage id="brandOnboarding.generating.approve" defaultMessage="Approve" />
            )}
          </button>
        </div>
      )}
    </div>
  );
}

export function BrandOnboardingGeneratingStage({
  brandId,
  batchId,
  sourceDomain,
}: {
  brandId: string;
  batchId: string | null;
  sourceDomain: string;
}) {
  const intl = useIntl();
  const utils = trpc.useUtils();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [active, setActive] = useState(0);
  const [approvingIds, setApprovingIds] = useState<Set<string>>(new Set());
  const [extraBatchId, setExtraBatchId] = useState<string | null>(null);
  const focusPath = buildBrandPath(brandId, "/focus");
  const reviewPath = buildBrandPath(brandId, "/review");
  const profileLabel = useMemo(() => formatInstagramProfileLabel(sourceDomain), [sourceDomain]);
  const billingAvailabilityQuery = trpc.brand.getBillingAvailability.useQuery(
    { brandId },
    { staleTime: 0 },
  );
  const autopilotQuery = trpc.brand.getAutopilotSetting.useQuery(
    { brandId },
    { staleTime: 0, refetchOnWindowFocus: true },
  );
  const strategyQuery = trpc.strategy.getMine.useQuery(
    { brandId },
    { staleTime: 0, refetchOnWindowFocus: true },
  );

  const batchQuery = trpc.standaloneAssetBatch.get.useQuery(
    { brandId, id: batchId ?? "" },
    {
      enabled: Boolean(batchId),
      refetchOnWindowFocus: true,
      refetchInterval: (query) =>
        areFirstPostsReady(normalizeStandaloneScheduledPosts(query.state.data?.scheduledPosts))
          ? false
          : 2000,
    },
  );
  const batchesQuery = trpc.standaloneAssetBatch.list.useQuery({ brandId });
  const extraBatchQuery = trpc.standaloneAssetBatch.get.useQuery(
    { brandId, id: extraBatchId ?? "" },
    {
      enabled: Boolean(extraBatchId),
      refetchOnWindowFocus: true,
      refetchInterval: (query) => {
        const extraPost = normalizeStandaloneScheduledPosts(query.state.data?.scheduledPosts)[0];
        return extraPost && isPostPreviewReady(extraPost) ? false : 2000;
      },
    },
  );
  const invalidateBatch = async () => {
    const invalidations = [
      utils.standaloneAssetBatch.get.invalidate({ brandId, id: batchId ?? "" }),
      utils.calendar.list.invalidate(),
    ];

    if (extraBatchId) {
      invalidations.push(utils.standaloneAssetBatch.get.invalidate({ brandId, id: extraBatchId }));
    }

    await Promise.all(invalidations);
  };
  const approveScheduledPost = trpc.calendar.approve.useMutation({ onSuccess: invalidateBatch });
  const updateAutopilot = trpc.brand.updateAutopilotSetting.useMutation({
    onSuccess: async () => {
      await Promise.all([
        utils.brand.getAutopilotSetting.invalidate({ brandId }),
        utils.campaign.nextGenerationCheck.invalidate({ brandId }),
      ]);
    },
  });
  const updateStrategy = trpc.strategy.updateMine.useMutation({
    onSuccess: async () => {
      await Promise.all([
        utils.strategy.getMine.invalidate({ brandId }),
        utils.calendar.list.invalidate(),
      ]);
    },
  });
  const posts = useMemo(
    () => normalizeStandaloneScheduledPosts(batchQuery.data?.scheduledPosts).slice(0, 2),
    [batchQuery.data?.scheduledPosts],
  );
  const extraPosts = useMemo(
    () => normalizeStandaloneScheduledPosts(extraBatchQuery.data?.scheduledPosts),
    [extraBatchQuery.data?.scheduledPosts],
  );
  const extraPost = extraPosts[0] ?? null;
  const extraPostReady = Boolean(extraPost && isPostPreviewReady(extraPost));
  const extraDraftRequested = Boolean(extraBatchId);
  const billingEnabled = billingAvailabilityQuery.data?.enabled ?? false;
  const upgradeStep = searchParams.get(UPGRADE_QUERY_KEY) as UpgradeStep | null;
  const postingRhythmLoading = autopilotQuery.isPending || strategyQuery.isPending;
  const postingRhythmSaving = updateAutopilot.isPending || updateStrategy.isPending;
  const strategyState = strategyQuery.data ? toStrategyEditorState(strategyQuery.data) : null;

  const setUpgradeSearchParams = (step: UpgradeStep | null, plan?: BillingPlanId | null) => {
    const nextParams = new URLSearchParams(searchParams);

    if (step) {
      nextParams.set(UPGRADE_QUERY_KEY, step);
    } else {
      nextParams.delete(UPGRADE_QUERY_KEY);
    }

    if (plan) {
      nextParams.set(UPGRADE_PLAN_QUERY_KEY, plan);
    } else {
      nextParams.delete(UPGRADE_PLAN_QUERY_KEY);
    }

    setSearchParams(nextParams, { replace: true });
  };

  const closeUpgradeModal = () => setUpgradeSearchParams(null);

  useEffect(() => {
    if (extraBatchId || !batchesQuery.data) {
      return;
    }

    const existingExtraBatch = batchesQuery.data.find(
      (batch) =>
        batch.id !== batchId && batch.prompt.includes(ONBOARDING_EXTRA_DRAFT_PROMPT_MARKER),
    );

    if (existingExtraBatch) {
      setExtraBatchId(existingExtraBatch.id);
    }
  }, [batchId, batchesQuery.data, extraBatchId]);

  useEffect(() => {
    if (billingEnabled || !upgradeStep) {
      return;
    }

    const nextParams = new URLSearchParams(searchParams);
    nextParams.delete(UPGRADE_QUERY_KEY);
    nextParams.delete(UPGRADE_PLAN_QUERY_KEY);
    setSearchParams(nextParams, { replace: true });
  }, [billingEnabled, upgradeStep, searchParams, setSearchParams]);

  // Read from cache synchronously so the very first render knows if the full previews exist.
  const postsReady = areFirstPostsReady(posts);

  const cachedPostsOnMount = useRef(
    batchId
      ? normalizeStandaloneScheduledPosts(
          utils.standaloneAssetBatch.get.getData({ brandId, id: batchId })?.scheduledPosts,
        )
      : [],
  );
  // If complete post previews are already in cache on mount, skip the animation entirely.
  const postsReadyOnMount = useRef(areFirstPostsReady(cachedPostsOnMount.current));
  const [animationDone, setAnimationDone] = useState(() => postsReadyOnMount.current);

  // Also skip animation if posts arrive on the very first query response.
  useEffect(() => {
    if (postsReady) setAnimationDone(true);
  }, [postsReady]);

  useEffect(() => {
    if (postsReadyOnMount.current) return;

    const timers = STEPS.map((_, index) => {
      if (index === 0) return null;
      return window.setTimeout(() => setActive(index), STEP_MS * index);
    }).filter(Boolean) as number[];
    const doneTimer = window.setTimeout(
      () => setAnimationDone(true),
      STEP_MS * STEPS.length + HOLD_MS,
    );

    return () => {
      timers.forEach((timer) => window.clearTimeout(timer));
      window.clearTimeout(doneTimer);
    };
  }, []);

  const showPreviewPage = animationDone && postsReady;

  if (!showPreviewPage) {
    // If animation finished but posts aren't ready yet, hold on the last step still active.
    const displayActive = animationDone ? STEPS.length - 1 : active;
    return (
      <div className="mx-auto w-full max-w-6xl px-6 py-8">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -12 }}
          transition={{ duration: 0.4 }}
          data-testid="stage-generating"
          style={{
            display: "grid",
            gridTemplateColumns: "minmax(0, 1fr)",
            gap: 48,
            alignItems: "start",
          }}
          className="ofast-grid"
        >
          <style>{`
          @media (min-width: 980px) { .ofast-grid { grid-template-columns: 1fr 1.05fr !important; gap: 56px !important } }
        `}</style>

          <div data-testid="generating-left">
            <h1
              style={{
                fontWeight: 800,
                fontSize: "clamp(28px, 3.6vw, 40px)",
                letterSpacing: "-0.03em",
                lineHeight: 1.05,
                color: NAVY,
                margin: 0,
              }}
            >
              <FormattedMessage
                id="brandOnboarding.generating.creatingYour"
                defaultMessage="Creating your"
              />{" "}
              <em
                style={{
                  fontFamily: FONT_SERIF,
                  fontStyle: "italic",
                  fontWeight: 400,
                  opacity: 0.6,
                }}
              >
                <FormattedMessage
                  id="brandOnboarding.generating.firstPostsWithPeriod"
                  defaultMessage="first posts."
                />
              </em>
            </h1>
            <p
              style={{
                marginTop: 12,
                fontSize: 15,
                lineHeight: 1.55,
                color: TEXT_MUTED,
                maxWidth: 480,
              }}
            >
              <FormattedMessage
                id="brandOnboarding.generating.description"
                defaultMessage="Shaping content to match your brand."
              />
            </p>

            <div style={{ marginTop: 20, display: "flex", flexDirection: "column", gap: 2 }}>
              {STEPS.map((step, index) => {
                const isDone = index < displayActive;
                const isActive = index === displayActive;

                if (isDone) {
                  return (
                    <motion.div
                      key={step.labelId}
                      initial={{ opacity: 0.4 }}
                      animate={{ opacity: 0.55 }}
                      transition={{ duration: 0.3 }}
                      data-testid={`generating-step-${index}`}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 10,
                        padding: "8px 12px",
                      }}
                    >
                      <div
                        style={{
                          width: 16,
                          height: 16,
                          borderRadius: 999,
                          background: NAVY,
                          color: MINT,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          flexShrink: 0,
                        }}
                      >
                        <Check style={{ width: 10, height: 10 }} strokeWidth={3} />
                      </div>
                      <div style={{ fontSize: 13, color: NAVY, fontWeight: 500 }}>
                        {intl.formatMessage({
                          id: step.labelId,
                          defaultMessage: step.labelDefault,
                        })}
                      </div>
                    </motion.div>
                  );
                }

                return (
                  <motion.div
                    key={step.labelId}
                    initial={{ opacity: 0.3 }}
                    animate={{ opacity: isActive ? 1 : 0.28 }}
                    transition={{ duration: 0.3 }}
                    data-testid={`generating-step-${index}`}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 12,
                      padding: isActive ? "11px 14px" : "8px 12px",
                      borderRadius: 12,
                      background: isActive
                        ? "linear-gradient(180deg, rgba(200,255,0,0.14), rgba(200,255,0,0.06))"
                        : "transparent",
                      border: "1px solid transparent",
                      transition: "all .25s ease",
                      position: "relative",
                      overflow: "hidden",
                    }}
                  >
                    <div
                      style={{
                        width: isActive ? 30 : 16,
                        height: isActive ? 30 : 16,
                        borderRadius: 999,
                        background: isActive ? "#fff" : "transparent",
                        boxShadow: isActive
                          ? "0 0 0 1px rgba(13,31,45,0.06), 0 4px 10px rgba(13,31,45,0.06)"
                          : "none",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        color: NAVY,
                        flexShrink: 0,
                        transition: "all .25s ease",
                      }}
                    >
                      {isActive ? (
                        <Sparkles style={{ width: 15, height: 15 }} strokeWidth={1.75} />
                      ) : (
                        <span
                          style={{
                            width: 4,
                            height: 4,
                            borderRadius: 999,
                            background: "rgba(13,31,45,0.3)",
                            display: "inline-block",
                          }}
                        />
                      )}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div
                        style={{
                          fontSize: isActive ? 14.5 : 13,
                          fontWeight: isActive ? 700 : 500,
                          color: NAVY,
                          letterSpacing: isActive ? "-0.005em" : 0,
                        }}
                      >
                        {intl.formatMessage({
                          id: step.labelId,
                          defaultMessage: step.labelDefault,
                        })}
                      </div>
                      {isActive ? (
                        <div
                          style={{
                            fontSize: 12,
                            color: TEXT_MUTED,
                            marginTop: 3,
                            display: "flex",
                            alignItems: "center",
                            gap: 6,
                          }}
                        >
                          <span>
                            {intl.formatMessage({
                              id: step.helperId,
                              defaultMessage: step.helperDefault,
                            })}
                          </span>
                          <span
                            style={{ display: "flex", alignItems: "center", gap: 3 }}
                            aria-hidden="true"
                          >
                            {[0, 1, 2].map((dot) => (
                              <motion.span
                                key={dot}
                                animate={{ opacity: [0.25, 1, 0.25], y: [0, -1, 0] }}
                                transition={{ duration: 1.2, repeat: Infinity, delay: dot * 0.15 }}
                                style={{
                                  width: 3,
                                  height: 3,
                                  borderRadius: 999,
                                  background: NAVY,
                                  display: "inline-block",
                                }}
                              />
                            ))}
                          </span>
                        </div>
                      ) : null}
                    </div>
                  </motion.div>
                );
              })}
            </div>
          </div>

          <div className="ofast-mock-wrap" style={{ position: "relative" }}>
            <style>{`
            @media (min-width: 980px) { .ofast-mock-wrap { margin-top: 78px } }
          `}</style>
            <span
              aria-hidden
              style={{
                pointerEvents: "none",
                position: "absolute",
                right: -32,
                top: -40,
                width: 240,
                height: 240,
                borderRadius: "50%",
                background: "rgba(200,255,0,0.2)",
                filter: "blur(48px)",
              }}
            />
            <span
              aria-hidden
              style={{
                pointerEvents: "none",
                position: "absolute",
                bottom: -48,
                left: -40,
                width: 256,
                height: 256,
                borderRadius: "50%",
                background: "#F4ECE0",
                filter: "blur(48px)",
              }}
            />
            <div style={{ position: "relative" }}>
              <GeneratingDraftsMock active={displayActive + 1} totalSteps={STEPS.length} />
            </div>
          </div>
        </motion.div>
      </div>
    );
  }

  if (!batchId) return null;
  if (batchQuery.isPending) {
    return (
      <CenteredLoader
        label={intl.formatMessage({
          id: "brandOnboarding.generating.loadingFirstPosts",
          defaultMessage: "Loading your first posts...",
        })}
      />
    );
  }

  const approvedCount = posts.filter((p) => p.approvalStatus === "approved").length;
  const handleSavePostingRhythm = async (input: {
    autopilotEnabled: boolean;
    postsPerDay: number;
  }) => {
    if (!strategyState) {
      toast.error(
        intl.formatMessage({
          id: "brandOnboarding.generating.postingRhythmMissingStrategy",
          defaultMessage: "Could not load posting settings.",
        }),
      );
      return;
    }

    try {
      const work: Array<Promise<unknown>> = [];

      if ((autopilotQuery.data?.enabled ?? true) !== input.autopilotEnabled) {
        work.push(updateAutopilot.mutateAsync({ brandId, enabled: input.autopilotEnabled }));
      }

      if (strategyState.postsPerDay !== input.postsPerDay) {
        work.push(
          updateStrategy.mutateAsync({
            brandId,
            strategy: {
              postsPerDay: input.postsPerDay,
              contentPillars: strategyState.contentPillars,
              campaignMix: strategyState.campaignMix,
            },
          }),
        );
      }

      await Promise.all(work);
      navigate(focusPath, { replace: true });
    } catch {
      toast.error(
        intl.formatMessage({
          id: "brandOnboarding.generating.postingRhythmSaveError",
          defaultMessage: "Could not save your posting rhythm.",
        }),
      );
    }
  };
  const extraDraftCard =
    extraPost && extraPostReady ? (
      <PostCardWithOverlay
        key={extraPost.id}
        post={extraPost}
        preview={resolveScheduledPostPreview({ output: extraPost.output, draft: extraPost.draft })}
        timeLabel={formatPostTimeLabel(extraPost.scheduledFor)}
        profileLabel={profileLabel}
        actions={getCalendarPostReviewActions({
          id: extraPost.id,
          scheduledFor: extraPost.scheduledFor,
          status: extraPost.status,
          approvalStatus: extraPost.approvalStatus,
          owner: {
            type: "standalone",
            id: extraBatchId ?? "",
            name: intl.formatMessage({
              id: "brandOnboarding.generating.extraDraftOwner",
              defaultMessage: "Extra onboarding draft",
            }),
          },
        })}
        editPath={buildFocusedPostEditorPath({
          brandId,
          owner: { type: "standalone", id: extraBatchId ?? "" },
          postId: extraPost.id,
        })}
        approving={approvingIds.has(extraPost.id)}
        rejecting={false}
        onApprove={() => {
          setApprovingIds((prev) => new Set(prev).add(extraPost.id));
          approveScheduledPost.mutate(
            { brandId, scheduledPostId: extraPost.id },
            {
              onSettled: () =>
                setApprovingIds((prev) => {
                  const next = new Set(prev);
                  next.delete(extraPost.id);
                  return next;
                }),
            },
          );
        }}
      />
    ) : extraBatchQuery.error ? (
      <ExtraDraftRetryCard
        key="extra-draft-retry"
        errorMessage={extraBatchQuery.error.message}
        onRetry={() => {
          void extraBatchQuery.refetch();
        }}
      />
    ) : extraDraftRequested ? (
      <ExtraDraftLoadingCard key="extra-draft-loading" />
    ) : null;
  const upgradeCard =
    extraPostReady && billingEnabled ? (
      <UpgradeToGenerateMoreCard
        key="upgrade-to-generate-more"
        onOpen={() => setUpgradeSearchParams("plans")}
      />
    ) : null;

  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -12 }}
      transition={{ duration: 0.35 }}
      className="mx-auto w-full max-w-5xl space-y-6 px-6 py-8"
    >
      <div className="space-y-1">
        <h1 className="text-balance font-display text-4xl font-bold tracking-tight sm:text-5xl">
          <FormattedMessage
            id="brandOnboarding.generating.previewTitle"
            defaultMessage="Your first drafts are ready."
          />
        </h1>
        <p className="text-sm leading-6 text-muted-foreground">
          <FormattedMessage
            id="brandOnboarding.generating.previewDescription"
            defaultMessage="These drafts were generated from your website, tone, and audience."
          />
        </p>
      </div>

      <div className="space-y-3">
        <p className="font-mono text-[11px] font-semibold uppercase tracking-[0.16em] text-muted-foreground">
          <FormattedMessage
            id="brandOnboarding.generating.postsToReviewProgress"
            defaultMessage="Posts to review · {approvedCount}/{totalCount} approved"
            values={{ approvedCount, totalCount: posts.length }}
          />
        </p>
        <div className="grid gap-6 sm:grid-cols-2 xl:grid-cols-4">
          {posts.map((post) => {
            const preview = resolveScheduledPostPreview({ output: post.output, draft: post.draft });
            const actions = getCalendarPostReviewActions({
              id: post.id,
              scheduledFor: post.scheduledFor,
              status: post.status,
              approvalStatus: post.approvalStatus,
              owner: {
                type: "standalone",
                id: batchId ?? "",
                name: intl.formatMessage({
                  id: "brandOnboarding.generating.firstPosts",
                  defaultMessage: "First posts",
                }),
              },
            });
            const timeLabel = formatPostTimeLabel(post.scheduledFor);

            const editPath = buildFocusedPostEditorPath({
              brandId,
              owner: { type: "standalone", id: batchId ?? "" },
              postId: post.id,
            });
            const approving = approvingIds.has(post.id);
            return (
              <PostCardWithOverlay
                key={post.id}
                post={post}
                preview={preview}
                timeLabel={timeLabel}
                profileLabel={profileLabel}
                actions={actions}
                editPath={editPath}
                approving={approving}
                rejecting={false}
                onApprove={() => {
                  setApprovingIds((prev) => new Set(prev).add(post.id));
                  approveScheduledPost.mutate(
                    { brandId, scheduledPostId: post.id },
                    {
                      onSettled: () =>
                        setApprovingIds((prev) => {
                          const n = new Set(prev);
                          n.delete(post.id);
                          return n;
                        }),
                    },
                  );
                }}
              />
            );
          })}
          {extraDraftCard}
          {upgradeCard}
        </div>
      </div>

      <div className="flex flex-col items-center gap-3 pt-4 text-center">
        <Button size="lg" asChild>
          <Link to={buildBrandPath(brandId, "/pulse")}>
            <FormattedMessage
              id="brandOnboarding.generating.goToBrand"
              defaultMessage="Go to your brand"
            />
            <ArrowRight className="h-4 w-4" />
          </Link>
        </Button>
        <Link
          to={reviewPath}
          className="text-sm text-muted-foreground underline-offset-4 hover:underline"
        >
          <FormattedMessage
            id="brandOnboarding.generating.openApprovals"
            defaultMessage="Or open review"
          />
        </Link>
      </div>
      {upgradeStep === "plans" && billingEnabled ? (
        <BillingPlanModal
          currentPlan={null}
          title={intl.formatMessage({
            id: "brandOnboarding.generating.upgradeModalTitle",
            defaultMessage: "Unlock campaigns and batch creation",
          })}
          description={intl.formatMessage({
            id: "brandOnboarding.generating.upgradeModalDescription",
            defaultMessage:
              "Generate multiple posts at once, build coordinated campaigns, and keep your content calendar active.",
          })}
          onClose={closeUpgradeModal}
          onPlanPick={(planId) => setUpgradeSearchParams("posting-rhythm", planId)}
        />
      ) : null}
      {upgradeStep === "posting-rhythm" && billingEnabled ? (
        <OnboardingPostingRhythmModal
          autopilotEnabled={autopilotQuery.data?.enabled ?? true}
          postsPerDay={strategyState?.postsPerDay ?? 2}
          loading={postingRhythmLoading}
          saving={postingRhythmSaving}
          onClose={closeUpgradeModal}
          onSave={(input) => {
            void handleSavePostingRhythm(input);
          }}
        />
      ) : null}
    </motion.div>
  );
}
